import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import MalfunctionNotificationCommand from '../domain/models/malfunction-notification-command';
import { MalfunctionNotificationResponse } from '../domain/models/malfunction-notification-response';

const baseUrlV1 = '/api/v1/Settings';
const baseUrlV2 = '/api/v2/Settings';

enum MalfunctionNotificationQueryPaths {
  ReadMalfunctionNotifications = 'ReadMalfunctionNotifications',
  UpsertMalfunctionNotifications = 'UpsertMalfunctionNotifications',
}

const readMalfunctionNotifications = async (axiosInstance: AxiosInstance): Promise<MalfunctionNotificationResponse> => {
  const response = await axiosInstance.get(`${baseUrlV1}/${MalfunctionNotificationQueryPaths.ReadMalfunctionNotifications}`);
  return response.data;
};

const upsertMalfunctionNotifications = async (
  axiosInstance: AxiosInstance,
  body: MalfunctionNotificationCommand,
): Promise<MalfunctionNotificationResponse> => {
  const response = await axiosInstance.post(`${baseUrlV2}/${MalfunctionNotificationQueryPaths.UpsertMalfunctionNotifications}`, body);
  return response.data;
};

export const useGetMalfunctionNotifications = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [MalfunctionNotificationQueryPaths.ReadMalfunctionNotifications],
    queryFn: () => readMalfunctionNotifications(authenticatedInstance),
  });
};

export const useUpsertMalfunctionNotifications = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MalfunctionNotificationQueryPaths.UpsertMalfunctionNotifications],
    mutationFn: (body: MalfunctionNotificationCommand) => upsertMalfunctionNotifications(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MalfunctionNotificationQueryPaths.ReadMalfunctionNotifications],
      });
    },
  });
};
